"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ModelDecidedConvergenceEvaluator = exports.MinutesSinceLastCommittedEvaluator = exports.IterationsSinceLastCommittedEvaluator = exports.ConvergenceCriteriaEvaluator = exports.ControlComponent = exports.MarkComponent = exports.ControlResult = exports.NoOpControl = exports.MarkResult = exports.NoOpMark = exports.ConvergenceControlAction = exports.ConvergenceMatchAction = exports.ConvergenceMatch = void 0;
exports.createConvergenceCriteriaEvaluator = createConvergenceCriteriaEvaluator;
const zod_1 = require("zod");
const api_1 = require("./api");
exports.ConvergenceMatch = zod_1.z.discriminatedUnion("type", [
    zod_1.z.object({
        type: zod_1.z.literal("IterationsSinceLastCommitted"),
        stepIndex: zod_1.z.number().int().nonnegative(),
        timestamp: zod_1.z.number().nonnegative(),
        scoreValue: zod_1.z.number().nonnegative(),
        action: zod_1.z.enum(["observation", "job_halt", "n/a"]).default("n/a"),
        details: zod_1.z.object({
            currentIterations: zod_1.z.number().int().nonnegative(),
            lastCommittedStep: zod_1.z.number().int().nonnegative(),
            threshold: zod_1.z.number().int().nonnegative(),
        }),
    }),
    zod_1.z.object({
        type: zod_1.z.literal("MinutesSinceLastCommitted"),
        stepIndex: zod_1.z.number().int().nonnegative(),
        timestamp: zod_1.z.number().nonnegative(),
        scoreValue: zod_1.z.number().nonnegative(),
        action: zod_1.z.enum(["observation", "job_halt", "n/a"]).default("n/a"),
        details: zod_1.z.object({
            minutesSinceLastCommitted: zod_1.z.number().nonnegative(),
            lastCommittedTimestamp: zod_1.z.number().nonnegative(),
            threshold: zod_1.z.number().nonnegative(),
        }),
    }),
    zod_1.z.object({
        type: zod_1.z.literal("ModelDecidedConvergence"),
        stepIndex: zod_1.z.number().int().nonnegative(),
        timestamp: zod_1.z.number().nonnegative(),
        scoreValue: zod_1.z.number().nonnegative(),
        action: zod_1.z.enum(["observation", "job_halt", "n/a"]).default("n/a"),
        eventId: zod_1.z.string().optional(),
        eventHash: zod_1.z.string().optional(),
        details: zod_1.z.object({
            convergenceTimeoutMinutes: zod_1.z.number().positive().optional(),
        }),
        capturedProperties: zod_1.z.record(zod_1.z.unknown()).optional(),
    }),
    // @deprecated DeepPCBWorkaround convergence matches are no longer created.
    // This schema variant is kept for backward compatibility with legacy data only.
    zod_1.z.object({
        type: zod_1.z.literal("DeepPCBWorkaround"),
        stepIndex: zod_1.z.number().int().nonnegative(),
        timestamp: zod_1.z.number().nonnegative(),
        scoreValue: zod_1.z.number().nonnegative(),
        action: zod_1.z.enum(["observation", "job_halt", "n/a"]).default("n/a"),
        details: zod_1.z.object({
            reason: zod_1.z.string(),
            lastCommittedStep: zod_1.z.number().int().nonnegative(),
        }),
    }),
    zod_1.z.object({
        type: zod_1.z.literal("MaxInactivityWaitTimeout"),
        stepIndex: zod_1.z.number().int().nonnegative(),
        timestamp: zod_1.z.number().nonnegative(),
        scoreValue: zod_1.z.number().nonnegative(),
        action: zod_1.z.enum(["observation", "job_halt", "n/a"]).default("n/a"),
        details: zod_1.z.object({
            maxInactivityWaitTimeoutMinutes: zod_1.z.number().nonnegative(),
            reasonCode: zod_1.z.string(),
            reason: zod_1.z.string(),
            lastCommittedStep: zod_1.z.number().int().nonnegative(),
            creditsBurned: zod_1.z.number().nonnegative(),
        }),
    }),
]);
exports.ConvergenceMatchAction = zod_1.z.enum(["observation", "job_halt", "n/a"]);
exports.ConvergenceControlAction = zod_1.z.enum(["converged", "paused", "extended"]);
exports.NoOpMark = zod_1.z.object({
    type: zod_1.z.literal("NoOp"),
});
exports.MarkResult = zod_1.z.object({
    type: zod_1.z.literal("Mark"),
    match: exports.ConvergenceMatch,
});
exports.NoOpControl = zod_1.z.object({
    type: zod_1.z.literal("NoOp"),
});
exports.ControlResult = zod_1.z.object({
    type: zod_1.z.literal("Control"),
    action: exports.ConvergenceControlAction,
    reason: zod_1.z.string(),
});
exports.MarkComponent = zod_1.z.discriminatedUnion("type", [exports.NoOpMark, exports.MarkResult]);
exports.ControlComponent = zod_1.z.discriminatedUnion("type", [exports.NoOpControl, exports.ControlResult]);
class ConvergenceCriteriaEvaluator {
    config;
    constructor(config) {
        this.config = config;
    }
    createMarkResult(type, stepIndex, timestamp, scoreValue, action, details) {
        return {
            type: "Mark",
            match: {
                type,
                stepIndex,
                timestamp,
                scoreValue,
                action,
                details,
            },
        };
    }
    createControlResult(action, reason) {
        return {
            type: "Control",
            action,
            reason,
        };
    }
    createNoOpMark() {
        return {
            type: "NoOp",
        };
    }
    createNoOpControl() {
        return {
            type: "NoOp",
        };
    }
}
exports.ConvergenceCriteriaEvaluator = ConvergenceCriteriaEvaluator;
class IterationsSinceLastCommittedEvaluator extends ConvergenceCriteriaEvaluator {
    evaluate(context) {
        const { job, currentRevision, currentTimestamp } = context;
        const { threshold, scoring } = this.config;
        if (currentRevision.committed) {
            return [this.createNoOpMark(), this.createNoOpControl()];
        }
        const iterations = "iterations" in job ? job.iterations ?? [] : [];
        const lastCommittedStep = this.getLastCommittedStepFromIterations(iterations);
        const currentStep = currentRevision.order;
        const test = currentStep - lastCommittedStep > threshold;
        if (!test) {
            return [this.createNoOpMark(), this.createNoOpControl()];
        }
        const mode = scoring?.mode || "control";
        let markComponent = this.createNoOpMark();
        let controlComponent = this.createNoOpControl();
        if (mode === "mark" || mode === "mark_and_control") {
            const existingMatches = job.convergenceMatches || [];
            const hasExistingMatch = existingMatches.some((match) => match.type === "IterationsSinceLastCommitted");
            if (!hasExistingMatch) {
                const action = mode === "mark" ? "observation" : "job_halt";
                markComponent = this.createMarkResult("IterationsSinceLastCommitted", currentStep, currentTimestamp, scoring?.scoreValue ?? 1, action, {
                    currentIterations: currentStep,
                    lastCommittedStep,
                    threshold,
                });
            }
        }
        if (mode === "control" || mode === "mark_and_control") {
            controlComponent = this.createControlResult("converged", `Iterations since last committed (${currentStep - lastCommittedStep}) exceeded threshold (${threshold})`);
        }
        return [markComponent, controlComponent];
    }
    getLastCommittedStepFromIterations(iterations) {
        const committedIterations = iterations.filter((iter) => iter.committed);
        if (committedIterations.length === 0) {
            return 0;
        }
        return Math.max(...committedIterations.map((iter) => iter.order));
    }
}
exports.IterationsSinceLastCommittedEvaluator = IterationsSinceLastCommittedEvaluator;
class MinutesSinceLastCommittedEvaluator extends ConvergenceCriteriaEvaluator {
    evaluate(context) {
        const { job, currentRevision, currentTimestamp } = context;
        const { threshold, scoring } = this.config;
        if (currentRevision.committed) {
            return [this.createNoOpMark(), this.createNoOpControl()];
        }
        const iterations = "iterations" in job ? job.iterations ?? [] : [];
        const lastCommittedTimestamp = this.getLastCommittedTimestampFromIterations(iterations);
        const currentTimestampMillis = currentRevision.generatedOn;
        const test = lastCommittedTimestamp > 0
            ? currentTimestampMillis - lastCommittedTimestamp > threshold * 60 * 1000
            : false;
        if (!test) {
            return [this.createNoOpMark(), this.createNoOpControl()];
        }
        const mode = scoring?.mode || "control";
        let markComponent = this.createNoOpMark();
        let controlComponent = this.createNoOpControl();
        if (mode === "mark" || mode === "mark_and_control") {
            const existingMatches = job.convergenceMatches || [];
            const hasExistingMatch = existingMatches.some((match) => match.type === "MinutesSinceLastCommitted");
            if (!hasExistingMatch) {
                const minutesSinceLastCommitted = lastCommittedTimestamp > 0 ? (currentTimestampMillis - lastCommittedTimestamp) / (60 * 1000) : 0;
                const action = mode === "mark" ? "observation" : "job_halt";
                markComponent = this.createMarkResult("MinutesSinceLastCommitted", currentRevision.order, currentTimestamp, scoring?.scoreValue ?? 1, action, {
                    minutesSinceLastCommitted,
                    lastCommittedTimestamp,
                    threshold,
                });
            }
        }
        if (mode === "control" || mode === "mark_and_control") {
            const minutesElapsed = lastCommittedTimestamp > 0 ? (currentTimestampMillis - lastCommittedTimestamp) / (60 * 1000) : 0;
            controlComponent = this.createControlResult("converged", `Minutes since last committed (${minutesElapsed.toFixed(2)}) exceeded threshold (${threshold})`);
        }
        return [markComponent, controlComponent];
    }
    getLastCommittedTimestampFromIterations(iterations) {
        const committedIterations = iterations.filter((iter) => iter.committed);
        if (committedIterations.length === 0) {
            return 0;
        }
        const lastCommittedIteration = committedIterations.reduce((latest, current) => current.order > latest.order ? current : latest);
        return lastCommittedIteration.generatedOn || 0;
    }
}
exports.MinutesSinceLastCommittedEvaluator = MinutesSinceLastCommittedEvaluator;
class ModelDecidedConvergenceEvaluator extends ConvergenceCriteriaEvaluator {
    evaluate(context) {
        const { job, currentTimestamp, event } = context;
        const { scoring, recordingMode, capturePayloadProperties } = this.config;
        if (event.eventType !== "BoardConverged") {
            return [this.createNoOpMark(), this.createNoOpControl()];
        }
        const iterations = "iterations" in job ? job.iterations ?? [] : [];
        const lastCommittedStep = this.getLastCommittedStepFromIterations(iterations);
        const mode = scoring?.mode;
        let markComponent = this.createNoOpMark();
        let controlComponent = this.createNoOpControl();
        if (mode === "mark" || mode === "mark_and_control") {
            const existingMatches = job.convergenceMatches || [];
            const capturedProperties = this.captureProperties(event.payload, capturePayloadProperties);
            const eventHash = this.calculateEventHash(event.eventId, capturedProperties);
            const shouldRecord = this.shouldRecordEvent(existingMatches, recordingMode ?? "first_seen", capturedProperties);
            if (shouldRecord) {
                const action = mode === "mark" ? "observation" : "job_halt";
                markComponent = {
                    type: "Mark",
                    match: {
                        type: "ModelDecidedConvergence",
                        stepIndex: lastCommittedStep,
                        timestamp: currentTimestamp,
                        scoreValue: scoring?.scoreValue ?? 1,
                        action,
                        eventId: event.eventId,
                        eventHash,
                        details: {},
                        ...(capturedProperties && Object.keys(capturedProperties).length > 0
                            ? { capturedProperties }
                            : {}),
                    },
                };
            }
        }
        if (mode === "control" || mode === "mark_and_control") {
            controlComponent = this.createControlResult("converged", "Model decided convergence");
        }
        return [markComponent, controlComponent];
    }
    captureProperties(payload, propertiesToCapture) {
        if (!propertiesToCapture || propertiesToCapture.length === 0) {
            return {};
        }
        const captured = {};
        for (const prop of propertiesToCapture) {
            if (prop in payload) {
                captured[prop] = payload[prop];
            }
        }
        return captured;
    }
    calculateEventHash(eventId, capturedProperties) {
        const sortedProperties = Object.keys(capturedProperties)
            .sort()
            .reduce((acc, key) => {
            acc[key] = capturedProperties[key];
            return acc;
        }, {});
        return JSON.stringify({
            eventId,
            capturedProperties: sortedProperties,
        });
    }
    buildCanonicalDedupKey(recordingMode, capturedProperties) {
        if (recordingMode !== "all_deduped_events") {
            return undefined;
        }
        const sortedProperties = Object.keys(capturedProperties)
            .sort()
            .reduce((acc, key) => {
            acc[key] = capturedProperties[key];
            return acc;
        }, {});
        return JSON.stringify({
            capturedProperties: sortedProperties,
        });
    }
    shouldRecordEvent(existingMatches, recordingMode, capturedProperties) {
        const modelDecidedMatches = existingMatches.filter((match) => match.type === "ModelDecidedConvergence");
        if (recordingMode === "first_seen") {
            return modelDecidedMatches.length === 0;
        }
        if (recordingMode === "all_events") {
            return true;
        }
        const currentDedupKey = this.buildCanonicalDedupKey(recordingMode, capturedProperties);
        return !modelDecidedMatches.some((match) => {
            if (match.type !== "ModelDecidedConvergence") {
                return false;
            }
            const existingDedupKey = this.buildCanonicalDedupKey(recordingMode, match.capturedProperties ?? {});
            return existingDedupKey === currentDedupKey;
        });
    }
    getLastCommittedStepFromIterations(iterations) {
        const committedIterations = iterations.filter((iter) => iter.committed);
        if (committedIterations.length === 0) {
            return 0;
        }
        return Math.max(...committedIterations.map((iter) => iter.order));
    }
}
exports.ModelDecidedConvergenceEvaluator = ModelDecidedConvergenceEvaluator;
function createConvergenceCriteriaEvaluator(criteria) {
    switch (criteria.type) {
        case api_1.ConvergenceCriteriaTypeEnum.enum.IterationsSinceLastCommitted:
            return new IterationsSinceLastCommittedEvaluator(criteria);
        case api_1.ConvergenceCriteriaTypeEnum.enum.MinutesSinceLastCommitted:
            return new MinutesSinceLastCommittedEvaluator(criteria);
        case api_1.ConvergenceCriteriaTypeEnum.enum.ModelDecidedConvergence:
            return new ModelDecidedConvergenceEvaluator(criteria);
        default: {
            const _exhaustive = criteria;
            throw new Error(`Unknown convergence criteria type: ${JSON.stringify(_exhaustive)}`);
        }
    }
}
//# sourceMappingURL=convergence.js.map